import React, {useEffect, useState} from 'react';
import {AppState, Linking, Platform, StyleSheet, Text, TouchableOpacity, View} from 'react-native';
import {QUIKLEARN_BACKEND_URL} from 'config/app.config';
import {appPlatform, appVersion, BUILD_ID} from 'utils/appUpdate/appVersion';
import {fetchUpdatePolicy, isBelowMinimum, platformPolicy} from 'utils/appUpdate/updatePolicy';
import {installApiVersionHeaders, onUpgradeRequired} from 'utils/appUpdate/apiVersionHeaders';
import {FONTS, TYPE} from 'style/typography';

const DEFAULT_MESSAGE = 'A new version of Quiklearn is available. Please update to continue.';
const BUILD_CHECK_MS = 5 * 60 * 1000;

/**
 * Forced update (QUI-183, Sol forced-update.verdict). The app reads its platform's minimum version on launch and
 * whenever it comes back to the foreground; below it - or when the API answers 426 - it blocks with one clear way to
 * update. Nothing reloads on its own: the learner presses the button, so a lesson is never cut off (Sol: reload "only
 * at a safe boundary"), and a policy that cannot be read blocks nobody.
 *
 * Web also checks /version.json every few minutes: a newer deployed build gets the service worker refreshed so the
 * next page load (every world change is one) runs it.
 */
export async function refreshServiceWorker() {
  try {
    const registration = await navigator.serviceWorker?.getRegistration?.();
    if (registration) await registration.update();
  } catch (_) {
    // No service worker, or the update check failed: the next load still asks the network for the page.
  }
}

async function checkForNewerBuild() {
  if (!BUILD_ID) return;
  try {
    const response = await fetch(`/version.json?t=${Date.now()}`, {cache: 'no-store'});
    if (!response.ok) return;
    const deployed = await response.json();
    if (deployed && deployed.buildId && deployed.buildId !== BUILD_ID) await refreshServiceWorker();
  } catch (_) {
    // Offline or not deployed with a version file: nothing to do.
  }
}

export default function UpdateGate() {
  const [required, setRequired] = useState(null);

  useEffect(() => {
    const platform = appPlatform();
    const version = appVersion();
    installApiVersionHeaders({baseUrl: QUIKLEARN_BACKEND_URL, platform, version});
    let alive = true;

    const check = async () => {
      const policy = await fetchUpdatePolicy(QUIKLEARN_BACKEND_URL);
      if (!alive || !policy || !isBelowMinimum(policy, platform, version)) return;
      const entry = platformPolicy(policy, platform) || {};
      setRequired({message: entry.message || DEFAULT_MESSAGE, updateUrl: entry.updateUrl || null});
    };
    check();

    const stopUpgradeListener = onUpgradeRequired(detail =>
      setRequired(current => current || {message: detail.message || DEFAULT_MESSAGE, updateUrl: detail.updateUrl || null}),
    );
    const appState = AppState.addEventListener ? AppState.addEventListener('change', state => state === 'active' && check()) : null;

    let onVisible = null;
    let timer = null;
    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      onVisible = () => {
        if (document.visibilityState !== 'visible') return;
        check();
        checkForNewerBuild();
      };
      document.addEventListener('visibilitychange', onVisible);
      timer = setInterval(checkForNewerBuild, BUILD_CHECK_MS);
    }

    return () => {
      alive = false;
      stopUpgradeListener();
      if (appState && appState.remove) appState.remove();
      if (onVisible) document.removeEventListener('visibilitychange', onVisible);
      if (timer) clearInterval(timer);
    };
  }, []);

  if (!required) return null;

  const update = async () => {
    if (Platform.OS === 'web') {
      await refreshServiceWorker();
      window.location.reload();
    } else if (required.updateUrl) {
      Linking.openURL(required.updateUrl).catch(() => {});
    }
  };

  return (
    <View style={styles.backdrop} testID="update-required">
      <View style={styles.card} accessibilityRole="alert">
        <Text style={styles.title}>Update required</Text>
        <Text style={styles.message}>{required.message}</Text>
        {(Platform.OS === 'web' || required.updateUrl) && (
          <TouchableOpacity style={styles.button} onPress={update} testID="update-required-button" accessibilityRole="button">
            <Text style={styles.buttonText}>{Platform.OS === 'web' ? 'Reload to update' : 'Update'}</Text>
          </TouchableOpacity>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    position: Platform.OS === 'web' ? 'fixed' : 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 100000,
    elevation: 100,
    backgroundColor: 'rgba(8, 12, 20, 0.88)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  card: {maxWidth: 420, width: '100%', backgroundColor: '#11161F', borderRadius: 16, padding: 28, alignItems: 'center'},
  title: {color: '#FFFFFF', fontFamily: FONTS.heading, fontSize: TYPE['2xl'], fontWeight: '700', marginBottom: 12, textAlign: 'center'},
  message: {color: '#C9D1DC', fontFamily: FONTS.body, fontSize: TYPE.base, lineHeight: 22, textAlign: 'center', marginBottom: 22},
  button: {backgroundColor: '#C6E936', borderRadius: 999, paddingVertical: 12, paddingHorizontal: 28},
  buttonText: {color: '#0B0F14', fontFamily: FONTS.body, fontSize: TYPE.base, fontWeight: '700'},
});
